// Trainee quiz page. The phone opens it from the quiz QR on the slide: ?quiz=2.
// Words: TRAINEE_QUIZ_WORDS.md. Mockup: MOCKUP_TRAINEE_QUIZ_APPROVED.html (2026-10-05).
// No sign-in. The trainee types the bag tag ID. Every rule is checked on the server.
// Nothing is saved until Submit. A reload before Submit starts the quiz over.

import { steps } from "./step-back";
import { callServer } from "./firebase";
import { NODES, QUIZZES, nodeById } from "./training";
import { normalizeTag, quizIdFromParam, quizStop, type Stop } from "./training-server";
import {
  busyScreen,
  correctedScreen,
  correctionListScreen,
  correctionQuestionScreen,
  errorScreenQz,
  landingScreen,
  myTrainingScreen,
  passedScreen,
  questionScreen,
  rulesScreen,
  stopScreen,
  whoScreen,
  type CheckResult,
  type LoadOk,
  type LoadResult,
  type MissedForCorrection,
  type NodeDates,
  type SubmitResult,
} from "./quiz-screens";

export interface QuizParams {
  quiz: string;
  trn: string;
  company: string;
  tag: string;
}

export function quizParamsFrom(search: string): QuizParams | null {
  const p = new URLSearchParams(search);
  if (!p.has("quiz") && !p.has("trn")) return null;
  return {
    quiz: (p.get("quiz") ?? "").trim(),
    trn: (p.get("trn") ?? "").trim(),
    company: (p.get("company") ?? p.get("co") ?? "").trim(),
    tag: (p.get("tag") ?? p.get("qewId") ?? "").trim(),
  };
}

/** A stop known from the link alone: an old TRN code or a quiz number that does not exist. */
export function stopFromLink(params: QuizParams): Stop | null {
  const quizId = quizIdFromParam(params.quiz);
  if (params.trn === "" && quizId) return null;
  return quizStop({
    legacyTrn: params.trn !== "",
    quizId,
    quiz: null,
    activeQuestionCount: 0,
    tagId: "",
    tag: { exists: false, status: "" },
    siteName: "",
    hasOpenSession: false,
    records: [],
    today: "",
  });
}

interface Correction {
  attemptId: string;
  first: { scorePct: number; questionsCorrect: number; questionsTotal: number };
  missed: MissedForCorrection[];
}

export function mountQuizPage(root: HTMLElement, params: QuizParams): void {
  const quizId = quizIdFromParam(params.quiz);
  const known = quizId ? QUIZZES.find((q) => q.quizId === quizId) : undefined;
  const landingQuiz = known
    ? { quizName: known.quizName, nodeName: nodeById(known.nodeId).nodeName, nodeNumber: String(NODES.findIndex((n) => n.nodeId === known.nodeId) + 1) }
    : null;
  const quizName = known?.quizName ?? "Knowledge Check";
  const fromTagPage = params.tag !== "";

  let tag = params.tag ? normalizeTag(params.tag) : "";
  let load: LoadOk | null = null;
  let qIndex = 0;
  let picks: Record<string, string> = {};
  let correction: Correction | null = null;
  let fixing: { questionId: string; selected?: string; wrong: boolean; error: string } | null = null;
  let records: NodeDates[] = [];
  let busy = false;

  /**
   * Shows a screen. depth is how many Back steps it sits past the screen before the quiz:
   * question n is n steps, a correction is one step past the list (approved 2026-10-09).
   * Every other screen is zero, so Back never walks into a finished quiz.
   */
  function show(html: string, depth = 0, onBack: () => void = () => undefined): void {
    steps().set(depth, onBack);
    root.innerHTML = html;
    window.scrollTo(0, 0);
  }

  function restart(keepTag: boolean): void {
    load = null;
    correction = null;
    fixing = null;
    picks = {};
    qIndex = 0;
    if (!keepTag) tag = "";
    show(landingScreen(landingQuiz, tag));
  }

  function addRecord(r: { nodeId: NodeDates["nodeId"]; completedOn: string | null; expiresOn: string | null }): void {
    if (r.completedOn) records = [...records, { nodeId: r.nodeId, completedOn: r.completedOn, expiresOn: r.expiresOn }];
  }

  function showList(): void {
    if (!load || !correction) return;
    fixing = null;
    show(correctionListScreen(load.quiz.quizName, load.quiz.nodeId, correction.first, correction.missed));
  }

  function showFix(): void {
    if (!load || !correction || !fixing) return;
    const open = correction.missed.filter((m) => !m.corrected);
    const m = correction.missed.find((x) => x.questionId === fixing!.questionId);
    if (!m) return showList();
    const position = correction.missed.length - open.length + 1;
    show(correctionQuestionScreen(load.quiz.quizName, m, position, correction.missed.length, fixing.selected, fixing.wrong, fixing.error), 1, () => {
      if (!busy) showList();
    });
  }

  function showQuestion(): void {
    const items = load?.questions ?? [];
    const item = items[qIndex];
    if (!load || !item) return;
    // Back goes to the previous question with his answer kept. From the first question, back to the rules.
    show(questionScreen(load.quiz, item, qIndex, items.length, picks[item.questionId]), qIndex + 1, prevQuestion);
  }

  /** The on-screen Back and the browser Back do the same thing: the last question, answer kept. */
  function prevQuestion(): void {
    if (busy || !load) return;
    if (qIndex > 0) {
      qIndex -= 1;
      showQuestion();
    } else show(rulesScreen(load.quiz));
  }

  function afterWho(): void {
    if (!load) return;
    if (load.pendingCorrection) {
      const p = load.pendingCorrection;
      correction = { attemptId: p.attemptId, first: p, missed: p.missed };
      showList();
      return;
    }
    show(rulesScreen(load.quiz));
  }

  async function doLoad(): Promise<void> {
    if (!tag) {
      show(landingScreen(landingQuiz, "", "Enter your bag tag ID, for example 01-QEW01."));
      return;
    }
    show(busyScreen("Finding your session"));
    try {
      const res = await callServer<LoadResult>("loadQuiz", {
        quiz: params.quiz,
        tagId: tag,
        ...(params.trn ? { trn: params.trn } : {}),
        ...(params.company ? { company: params.company } : {}),
      });
      if (!res.ok) {
        show(stopScreen(res.stop, quizName));
        return;
      }
      load = res;
      tag = res.tagId;
      records = [...res.records];
      if (fromTagPage) afterWho();
      else show(whoScreen(res));
    } catch (e) {
      const msg = (e as Error).message;
      // A bad tag comes back as a plain message. Keep the trainee on the tag field.
      if (msg.startsWith("Enter your bag tag ID")) show(landingScreen(landingQuiz, tag, msg));
      else show(errorScreenQz(msg, "retry-load"));
    }
  }

  async function doSubmit(): Promise<void> {
    if (!load) return;
    show(busyScreen("Grading your quiz"));
    try {
      const res = await callServer<SubmitResult>("submitQuiz", {
        quiz: load.quiz.quizId,
        tagId: load.tagId,
        picks,
        ...(params.company ? { company: params.company } : {}),
      });
      if (!res.ok) {
        show(stopScreen(res.stop, load.quiz.quizName));
        return;
      }
      if (res.isPassed) {
        addRecord(res);
        show(passedScreen(res, load.quiz.quizName));
        return;
      }
      correction = { attemptId: res.attemptId, first: res, missed: res.missed as MissedForCorrection[] };
      showList();
    } catch (e) {
      show(errorScreenQz((e as Error).message, "retry-submit"));
    }
  }

  async function doCheck(): Promise<void> {
    if (!load || !correction || !fixing?.selected) return;
    const pick = fixing.selected;
    const m = correction.missed.find((x) => x.questionId === fixing!.questionId);
    if (!m) return;
    show(busyScreen("Checking"));
    let res: CheckResult;
    try {
      res = await callServer<CheckResult>("checkCorrection", {
        tagId: load.tagId,
        attemptId: correction.attemptId,
        questionId: m.questionId,
        pick,
        ...(params.company ? { company: params.company } : {}),
      });
    } catch (e) {
      fixing = { ...fixing, error: (e as Error).message, wrong: false };
      showFix();
      return;
    }
    if (!res.ok) {
      fixing = { ...fixing, selected: undefined, error: res.message, wrong: false };
      showFix();
      return;
    }
    m.picks = [...m.picks, pick];
    if (!res.right) {
      fixing = { questionId: m.questionId, selected: undefined, wrong: true, error: "" };
      showFix();
      return;
    }
    m.corrected = true;
    if (res.finished) {
      addRecord(res);
      show(correctedScreen(load.quiz.quizName, res, load.sessionCode));
      return;
    }
    showList();
  }

  function run(task: () => Promise<void>): void {
    if (busy) return;
    busy = true;
    void task().finally(() => {
      busy = false;
    });
  }

  root.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const form = ev.target as HTMLFormElement;
    const input = form.elements.namedItem("tag") as HTMLInputElement | null;
    tag = normalizeTag(input?.value ?? "");
    run(doLoad);
  });

  root.addEventListener("click", (ev) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!el || (el as HTMLButtonElement).disabled || busy) return;
    const act = el.dataset.act;
    switch (act) {
      case "notme":
        restart(false);
        return;
      case "restart":
        restart(true);
        return;
      // Stop screens and My Training end the visit. The tag field comes back
      // empty, so Back never loops into the same stop and the next trainee
      // starts clean on a shared phone.
      case "done":
        restart(false);
        return;
      case "yes":
        afterWho();
        return;
      case "who":
        // Back from the rules: one step, to Is This You (NAV_AUDIT.md item 4).
        if (load) show(whoScreen(load));
        return;
      case "start":
        qIndex = 0;
        picks = {};
        showQuestion();
        return;
      case "pick": {
        const item = load?.questions?.[qIndex];
        if (item && el.dataset.key) picks[item.questionId] = el.dataset.key;
        showQuestion();
        return;
      }
      case "next":
        qIndex += 1;
        showQuestion();
        return;
      case "prev":
        prevQuestion();
        return;
      case "submit":
      case "retry-submit":
        run(doSubmit);
        return;
      case "retry-load":
        run(doLoad);
        return;
      case "fix":
        if (el.dataset.q) {
          fixing = { questionId: el.dataset.q, wrong: false, error: "" };
          showFix();
        }
        return;
      case "repick":
        if (fixing && el.dataset.key) {
          fixing = { ...fixing, selected: el.dataset.key, wrong: false, error: "" };
          showFix();
        }
        return;
      case "check":
        run(doCheck);
        return;
      case "list":
        showList();
        return;
      case "mytraining":
        if (load) show(myTrainingScreen(load.name, load.tagId, records, load.today));
        return;
      default:
        return;
    }
  });

  const linkStop = stopFromLink(params);
  if (linkStop) {
    show(stopScreen(linkStop, quizName));
    return;
  }
  if (fromTagPage) run(doLoad);
  else show(landingScreen(landingQuiz, tag));
}
